import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { packageQueries } from '@/services/packages/queries';
import { packageMutations } from '@/services/packages/mutations';
import { CreatePackageParams, UpdatePackageParams } from '@/features/packages/types';

export const usePackages = (
  providerId: number,
  versionId: number,
  regionId: number
) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['packages', providerId, versionId, regionId],
    queryFn: async () => {
      return await packageQueries.getPackages(providerId, versionId, regionId);
    }
  });

  const createPackageMutation = useMutation({
    mutationFn: async (data: CreatePackageParams) => {
      return await packageMutations.createPackage(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['packages', providerId, versionId, regionId]
      });
    }
  });

  const updatePackageMutation = useMutation({
    mutationFn: async (params: UpdatePackageParams) => {
      return await packageMutations.updatePackage(params);
    },
    onSuccess: (_, params) => {
      queryClient.invalidateQueries({
        queryKey: ['packages', providerId, versionId, regionId]
      });
      queryClient.invalidateQueries({
        queryKey: ['package', params.id]
      });
    }
  });

  const deletePackageMutation = useMutation({
    mutationFn: async (packageId: number) => {
      await packageMutations.deletePackage(packageId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['packages', providerId, versionId, regionId]
      });
    }
  });

  const addPlanMutation = useMutation({
    mutationFn: async ({ packageId, planId }: { packageId: number; planId: number }) => {
      await packageMutations.addPlanToPackage(packageId, planId, versionId);
    },
    onSuccess: (_, { packageId }) => {
      queryClient.invalidateQueries({
        queryKey: ['package', packageId]
      });
    }
  });

  const removePlanMutation = useMutation({
    mutationFn: async ({ packageId, planId }: { packageId: number; planId: number }) => {
      await packageMutations.removePlanFromPackage(packageId, planId);
    },
    onSuccess: (_, { packageId }) => {
      queryClient.invalidateQueries({
        queryKey: ['package', packageId]
      });
    }
  });

  const addCategoryMutation = useMutation({
    mutationFn: async ({ packageId, categoryId }: { packageId: number; categoryId: number }) => {
      await packageMutations.addCategoryToPackage(packageId, categoryId, versionId);
    },
    onSuccess: (_, { packageId }) => {
      queryClient.invalidateQueries({
        queryKey: ['package', packageId]
      });
    }
  });

  const removeCategoryMutation = useMutation({
    mutationFn: async ({ packageId, categoryId }: { packageId: number; categoryId: number }) => {
      await packageMutations.removeCategoryFromPackage(packageId, categoryId);
    },
    onSuccess: (_, { packageId }) => {
      queryClient.invalidateQueries({
        queryKey: ['package', packageId]
      });
    }
  });

  const addChannelMutation = useMutation({
    mutationFn: async ({ packageId, channelId }: { packageId: number; channelId: number }) => {
      await packageMutations.addChannelToPackage(packageId, channelId, versionId);
    },
    onSuccess: (_, { packageId }) => {
      queryClient.invalidateQueries({
        queryKey: ['package', packageId]
      });
    }
  });

  const removeChannelMutation = useMutation({
    mutationFn: async ({ packageId, channelId }: { packageId: number; channelId: number }) => {
      await packageMutations.removeChannelFromPackage(packageId, channelId);
    },
    onSuccess: (_, { packageId }) => {
      queryClient.invalidateQueries({
        queryKey: ['package', packageId]
      });
    }
  });

  return {
    // Query data
    packages: query.data || [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,

    // Mutations
    createPackage: createPackageMutation,
    updatePackage: updatePackageMutation,
    deletePackage: deletePackageMutation,
    addPlan: addPlanMutation,
    removePlan: removePlanMutation,
    addCategory: addCategoryMutation,
    removeCategory: removeCategoryMutation,
    addChannel: addChannelMutation,
    removeChannel: removeChannelMutation
  };
};

export const usePackageById = (packageId: number | null, versionId?: number) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['package', packageId, versionId],
    queryFn: async () => {
      if (!packageId) return null;
      return await packageQueries.getPackageById(packageId, versionId);
    },
    enabled: !!packageId
  });

  const updatePackageMutation = useMutation({
    mutationFn: async (params: UpdatePackageParams) => {
      return await packageMutations.updatePackage(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['package', packageId, versionId]
      });
    }
  });

  const addPlanMutation = useMutation({
    mutationFn: async ({ planId, versionId: version }: { planId: number; versionId: number }) => {
      if (!packageId) throw new Error('Package ID is required');
      await packageMutations.addPlanToPackage(packageId, planId, version);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['package', packageId, versionId]
      });
    }
  });

  const removePlanMutation = useMutation({
    mutationFn: async (planId: number) => {
      if (!packageId) throw new Error('Package ID is required');
      await packageMutations.removePlanFromPackage(packageId, planId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['package', packageId, versionId]
      });
    }
  });

  const addCategoryMutation = useMutation({
    mutationFn: async ({ categoryId, versionId: version }: { categoryId: number; versionId: number }) => {
      if (!packageId) throw new Error('Package ID is required');
      await packageMutations.addCategoryToPackage(packageId, categoryId, version);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['package', packageId, versionId]
      });
    }
  });

  const removeCategoryMutation = useMutation({
    mutationFn: async (categoryId: number) => {
      if (!packageId) throw new Error('Package ID is required');
      await packageMutations.removeCategoryFromPackage(packageId, categoryId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['package', packageId, versionId]
      });
    }
  });

  const addChannelMutation = useMutation({
    mutationFn: async ({ channelId, versionId: version }: { channelId: number; versionId: number }) => {
      if (!packageId) throw new Error('Package ID is required');
      await packageMutations.addChannelToPackage(packageId, channelId, version);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['package', packageId, versionId]
      });
    }
  });

  const removeChannelMutation = useMutation({
    mutationFn: async (channelId: number) => {
      if (!packageId) throw new Error('Package ID is required');
      await packageMutations.removeChannelFromPackage(packageId, channelId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['package', packageId, versionId]
      });
    }
  });

  return {
    // Query data
    package: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,

    // Mutations
    updatePackage: updatePackageMutation,
    addPlan: addPlanMutation,
    removePlan: removePlanMutation,
    addCategory: addCategoryMutation,
    removeCategory: removeCategoryMutation,
    addChannel: addChannelMutation,
    removeChannel: removeChannelMutation
  };
};
